/**
 * ActiveFilters.js
 *
 * A reusable React component for displaying active filters as Material-UI Chips.
 * Supports standard filters, date range filters, and numeric range filters.
 * Range filters are grouped visually with a border, and the filter label is shown once
 * per group. Handles any date or numeric range generically via filterDefinitions.
 *
 * Props:
 * - filterDefinitions: Array of filter objects defining type, id, label, and values.
 * - filterConfig: Configuration object for filter types.
 * - formatDate: Optional formatter for date values.
 * - onRemoveFilter: Handler for removing standard filters.
 * - onRemoveDateRange: Handler for removing dateRange filter fields.
 * - onRemoveNumericRange: Handler for removing numericRange filter fields.
 */

import { Chip, Box, Typography } from "@mui/material";
import { formatDate } from "../../Utils/DateUtils";

// Default filter type configuration
const defaultFilterConfig = {
  standard: {
    isValid: (filter) =>
      filter.value !== undefined &&
      filter.value !== null &&
      filter.value !== "",
    format: (filter) => `${filter.value}`,
    onRemove: (filter, onRemoveFilter) => onRemoveFilter(filter.id),
  },
  dateRange: {
    isValid: (filter) => filter.startValue || filter.endValue,
    format: (filter, formatDate) => {
      const startLabel = filter.startValue
        ? `Start: ${formatDate(filter.startValue)}`
        : "";
      const endLabel = filter.endValue
        ? `End: ${formatDate(filter.endValue)}`
        : "";
      return {
        startLabel,
        endLabel,
        startField: filter.startField,
        endField: filter.endField,
      };
    },
    onRemove: (filter, onRemoveDateRange, field) =>
      onRemoveDateRange(
        field === "start" ? filter.startField : filter.endField
      ),
  },
  numericRange: {
    isValid: (filter) =>
      (filter.minValue !== undefined &&
        filter.minValue !== null &&
        filter.minValue !== "") ||
      (filter.maxValue !== undefined &&
        filter.maxValue !== null &&
        filter.maxValue !== ""),
    format: (filter) => {
      const minLabel =
        filter.minValue !== undefined &&
        filter.minValue !== null &&
        filter.minValue !== ""
          ? `Min: ${filter.minValue}`
          : "";
      const maxLabel =
        filter.maxValue !== undefined &&
        filter.maxValue !== null &&
        filter.maxValue !== ""
          ? `Max: ${filter.maxValue}`
          : "";
      return {
        minLabel,
        maxLabel,
        minField: filter.minField,
        maxField: filter.maxField,
      };
    },
    onRemove: (filter, onRemoveNumericRange, field) =>
      onRemoveNumericRange(field === "min" ? filter.minField : filter.maxField),
  },
};

const ActiveFilters = ({
  filterDefinitions,
  filterConfig = defaultFilterConfig,
  onRemoveFilter,
  onRemoveDateRange,
  onRemoveNumericRange,
  formatDate: customFormatDate,
}) => {
  // Use provided formatDate or default
  const formatDateFn = customFormatDate || formatDate;

  // Check if any filter is active
  const hasAnyFilter = filterDefinitions.some((filter) => {
    const config = filterConfig[filter.type];
    return config && config.isValid(filter);
  });

  if (!hasAnyFilter) return null;

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        flexWrap: "wrap",
        gap: "12px",
        margin: "16px 0",
      }}
    >
      <Typography
        sx={{
          fontWeight: 600,
          marginLeft: "16px",
          marginRight: "8px",
          color: "#666",
        }}
      >
        Filtered:
      </Typography>

      {filterDefinitions.map((filter, index) => {
        const config = filterConfig[filter.type];
        if (!config || !config.isValid(filter)) return null;

        if (filter.type === "standard") {
          const value = config.format(filter);
          const isRemovable = filter.removable !== false;
          return (
            <FilterChip
              key={`${filter.id}-${index}`}
              label={
                <>
                  <Typography
                    sx={{
                      fontSize: "14px",
                      fontWeight: 500,
                    }}
                  >
                    {filter.label}:
                  </Typography>
                  <span style={{ fontWeight: 400, marginLeft: "4px" }}>
                    {value}
                  </span>
                </>
              }
              onDelete={
                isRemovable
                  ? () => config.onRemove(filter, onRemoveFilter)
                  : undefined
              }
            />
          );
        } else if (
          filter.type === "dateRange" ||
          filter.type === "numericRange"
        ) {
          const {
            minLabel,
            maxLabel,
            startLabel,
            endLabel,
            startField,
            endField,
            minField,
            maxField,
          } = config.format(filter, formatDateFn);
          const labels =
            filter.type === "dateRange"
              ? [
                  {
                    label: startLabel,
                    field: "start",
                    key: "start",
                    fieldName: startField,
                  },
                  {
                    label: endLabel,
                    field: "end",
                    key: "end",
                    fieldName: endField,
                  },
                ]
              : [
                  {
                    label: minLabel,
                    field: "min",
                    key: "min",
                    fieldName: minField,
                  },
                  {
                    label: maxLabel,
                    field: "max",
                    key: "max",
                    fieldName: maxField,
                  },
                ];

          const validLabels = labels.filter((item) => item.label);

          if (validLabels.length === 0) return null;

          return (
            <Box
              key={`${filter.id}-${index}`}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                border: "1px solid #e0e0e0",
                borderRadius: "4px",
                padding: "2px",
                backgroundColor: "#fafafa",
              }}
            >
              <Typography
                sx={{
                  fontSize: "14px",
                  fontWeight: 500,
                  color: "#333",
                  marginLeft: "8px",
                }}
              >
                {filter.label}:
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: "2px" }}>
                {validLabels.map((item) => (
                  <FilterChip
                    key={`${filter.id}-${item.key}`}
                    label={
                      <span
                        style={{
                          fontWeight: 400,
                        }}
                      >
                        {item.label}
                      </span>
                    }
                    onDelete={() =>
                      config.onRemove(
                        filter,
                        filter.type === "dateRange"
                          ? onRemoveDateRange
                          : onRemoveNumericRange,
                        item.field
                      )
                    }
                  />
                ))}
              </Box>
            </Box>
          );
        }
        return null;
      })}
    </Box>
  );
};

const FilterChip = ({ label, onDelete }) => {
  return (
    <Chip
      label={label}
      onDelete={onDelete}
      sx={{
        borderRadius: "4px",
        backgroundColor: "#f5f5f5",
        color: "#333",
        height: "auto",
        "& .MuiChip-label": {
          display: "flex",
          alignItems: "center",
          padding: "6px 12px",
          whiteSpace: "normal",
          "& > span > li": {
            fontSize: "12px",
            padding: 0,
          },
        },
        "& .MuiChip-deleteIcon": {
          color: "#999",
          "&:hover": {
            color: "#f44336",
          },
        },
      }}
    />
  );
};

export default ActiveFilters;
